import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ChevronLeft, FileTextIcon, Plus } from 'lucide-react'
import { ConciergeChat } from './ConciergeChat'
import { ConciergeSessionsPicker } from './ConciergeSessionsPicker'
import { PennReviewModal, type PennReviewModalSubmit } from './PennReviewModal'
import { CockpitTabBar } from '@/features/cockpit'
import {
  useConciergeStore,
  type ChatMode,
} from '@/stores/useConciergeStore'
import './concierge.css'

/**
 * Top-level page for /concierge. Standalone layout — does NOT use
 * AppShell (the chat owns the full viewport height for the scrollable
 * messages + sticky input).
 *
 * Visual language: matches lovellarmand.com's dark homepage exactly —
 * rgb(23,23,23) page bg, rgb(235,235,235) text, 4px raised status dots,
 * hairline borders, NO rounded corners, Atlas Grotesk (the React app's
 * default; visually adjacent to the homepage's Dazzed). The .concierge-
 * root scope overrides the React app's default light CSS variables so
 * Tailwind's `bg-background` / `text-foreground` classes inside the
 * tree resolve to the dark palette.
 */
export function ConciergePage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const enterMode = useConciergeStore((s) => s.enterMode)
  const newSession = useConciergeStore((s) => s.newSession)

  // Penn Review modal state. Trigger button sits next to the wrap-up walk
  // button in the header; submit hands the payload to enterMode('penn_review')
  // and the auto-greet effect in ConciergeChat fires the SSE stream.
  const [pennModalOpen, setPennModalOpen] = useState(false)

  const handlePennReviewSubmit = (p: PennReviewModalSubmit) => {
    setPennModalOpen(false)
    enterMode('penn_review', {
      dateFrom: p.dateFrom,
      dateTo: p.dateTo,
      contextNote: p.contextNote,
      writeToVault: p.writeToVault,
      displayTitle: `${p.dateFrom} → ${p.dateTo}`,
    })
  }

  // URL-driven mode entry. Lets the watcher's badge handler (or any
  // external surface) open the chat directly in a workflow room via
  // /concierge?mode=wrapup&block=<absolute path>&name=<title>. We
  // consume the params and strip them from the URL so a refresh doesn't
  // re-enter the same mode (the store's persist middleware already
  // handles refresh-survival for mode state).
  //
  // This watches `searchParams` (not mount-only) so a calendar wrap-up
  // link clicked while the web app is ALREADY OPEN is honored too. The
  // old `[]` deps meant ConciergePage never remounted on an in-tab
  // navigation, so the new block/name were silently ignored and the
  // chat kept whatever (stale) mode it had — the user clicked Monitor
  // Meadows but the previous block stayed loaded. Stripping the params
  // changes searchParams again, re-running this effect; the early return
  // on a missing `mode` makes that pass a no-op (no loop).
  useEffect(() => {
    const modeParam = searchParams.get('mode') as ChatMode | null
    if (!modeParam || modeParam === 'default') return
    if (!['wrapup', 'prep', 'review'].includes(modeParam)) return
    enterMode(modeParam, {
      blockPath: searchParams.get('block') || undefined,
      projectPath: searchParams.get('project') || undefined,
      displayTitle: searchParams.get('name') || undefined,
      reviewVariant: (searchParams.get('variant') as
        | 'daily'
        | 'weekly'
        | 'ninety_day'
        | null) || undefined,
      // The calendar wrap-up link should JUST RUN: the agent wraps the block
      // autonomously (streamed), then the pane stays open as a conversation
      // for the user to add notes and have the agent update the wrap-up card +
      // CONCIERGE.md. Only for wrap-up — prep/review keep their normal opener.
      autoRun: modeParam === 'wrapup',
    })
    // Strip the mode-related params, keep everything else intact.
    const next = new URLSearchParams(searchParams)
    for (const k of ['mode', 'block', 'project', 'name', 'variant']) {
      next.delete(k)
    }
    setSearchParams(next, { replace: true })
    // Re-run whenever the URL's search params change so an in-tab
    // navigation to a new calendar link is consumed, not just the first
    // mount. enterMode / setSearchParams are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  return (
    <div className="concierge-root h-dvh flex flex-col bg-background text-foreground">
      <header className="flex-shrink-0 border-b border-border">
        <div className="max-w-[680px] mx-auto px-5 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => navigate('/')}
              aria-label="Back to home"
              className="-ml-1 p-1 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            <div className="text-[15px] text-foreground">concierge</div>
            <span
              className="inline-block w-1 h-1 rounded-full bg-[#22c55e] -translate-y-1"
              aria-label="live"
            />
          </div>
          <div className="flex items-center gap-3">
            {/* New conversation — clears the current thread and starts a
                fresh default session (same as the sessions picker's
                "New conversation", surfaced as a one-click header affordance). */}
            <button
              type="button"
              onClick={() => newSession()}
              aria-label="New conversation"
              title="New conversation"
              className="flex items-center justify-center px-2 py-1 border border-border text-muted-foreground hover:text-foreground transition-colors"
            >
              <Plus size={14} />
            </button>
            {/* Penn Review trigger. PENN-specific (no other domains get
                a per-domain review button on this page). Opens a date-range
                modal; submit flips chatMode to penn_review and the chat's
                auto-greet effect fires the SSE stream. */}
            <button
              type="button"
              onClick={() => setPennModalOpen(true)}
              aria-label="Open Penn Review"
              title="Penn Review — pick a date range, review every PENN workstream with activity"
              className="flex items-center gap-1.5 text-[12px] px-2 py-1 border border-border text-muted-foreground hover:text-foreground transition-colors"
            >
              <FileTextIcon size={12} />
              <span>penn review</span>
            </button>
            <ConciergeSessionsPicker />
          </div>
        </div>
      </header>

      {/* pb-14 on mobile clears the fixed CockpitTabBar (h-14; the
          safe-area inset is already on .concierge-root's padding). */}
      <main className="flex-1 min-h-0 pb-14 md:pb-0">
        <ConciergeChat />
      </main>

      {/* Bottom tab bar shared with /cockpit (md:hidden) + capture sheet. */}
      <CockpitTabBar />

      <PennReviewModal
        open={pennModalOpen}
        onClose={() => setPennModalOpen(false)}
        onSubmit={handlePennReviewSubmit}
      />
    </div>
  )
}
